import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createVendor,
  fetchVendorPermits,
  fetchVendors,
} from '../api/govopsMunicipalService';
import { formatGovOpsError } from '../api/govopsErrors';
import { formatStatus } from '../constants/workflowLabels';
import PageShell from '../components/PageShell';
import GlobalSearch from '../components/GlobalSearch';

/** GOVOPS-1304 — alarm company vendor portal (pilot). */
export default function VendorPortalPage() {
  const [vendors, setVendors] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [permits, setPermits] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchVendors();
      setVendors(rows);
      if (rows.length && !selectedId) setSelectedId(rows[0].id);
    } catch (err) {
      setError(formatGovOpsError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    if (!selectedId) {
      setPermits([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchVendorPermits(selectedId);
        if (!cancelled) setPermits(rows);
      } catch (err) {
        if (!cancelled) setError(formatGovOpsError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    try {
      const vendor = await createVendor({
        name: name.trim(),
        contactEmail: email.trim() || undefined,
      });
      setName('');
      setEmail('');
      await loadVendors();
      setSelectedId(vendor.id);
    } catch (err) {
      setError(formatGovOpsError(err));
    }
  };

  return (
    <PageShell
      title="Vendor portal"
      subtitle="Alarm company permits linked to your organization (GovOps pilot)."
      className="rhs-vendor-portal"
    >
      <GlobalSearch className="rhs-mb-md" />

      <nav className="rhs-quick-nav" aria-label="Vendor navigation">
        <Link to="/admin" className="rhs-quick-nav-item">
          ← Operations dashboard
        </Link>
      </nav>

      {error && (
        <div className="rhs-error-panel" role="alert">
          {error}
        </div>
      )}

      <section className="rhs-card">
        <h2 className="rhs-card-title">Register vendor</h2>
        <form className="rhs-form-row" onSubmit={handleCreate}>
          <label>
            Company name
            <input
              className="rhs-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <label>
            Contact email
            <input
              className="rhs-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button type="submit" className="rhs-btn">
            Add vendor
          </button>
        </form>
      </section>

      {loading ? (
        <p className="rhs-muted">Loading vendors…</p>
      ) : (
        <section className="rhs-card">
          <h2 className="rhs-card-title">Linked permits</h2>
          {vendors.length === 0 ? (
            <p className="rhs-muted">No vendors yet. Create one above.</p>
          ) : (
            <>
              <label className="rhs-label">
                Vendor
                <select
                  className="rhs-input"
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              {permits.length === 0 ? (
                <p className="rhs-muted">
                  No permits linked to this vendor. Set alarm company on permit details.
                </p>
              ) : (
                <ul className="rhs-list-plain">
                  {permits.map((p) => (
                    <li key={p.id}>
                      <Link to={`/permit-details/${p.id}`}>
                        {p.permitType || p.id}
                      </Link>
                      {' — '}
                      {formatStatus(p.status)}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}
    </PageShell>
  );
}
